import { Node, mergeAttributes } from '@tiptap/core';
import { nanoid } from 'nanoid';

const checkboxIcon = `<svg class="prosemirror-checkbox-icon" width="100%" height="100%" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 6.09998L5 8.59998L10 3.59998" stroke="black" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

export interface CheckboxOptions {
  HTMLAttributes: Record<string, any>;

  // Whether to show an nimation when the checkbox is checked
  hasCheckedAnimation?: boolean;

  // Triggered when the checkbox is clicked
  onChange?: (checkboxId: string, isChecked: boolean) => void;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    checkbox: {
      /**
       * Add a checkbox to the editor
       */
      insertCheckbox: () => ReturnType;
    };
  }
}

export const Checkbox = Node.create<CheckboxOptions>({
  name: 'checkbox',

  group: 'inline',

  inline: true,

  selectable: true,

  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
      },
      isChecked: {
        default: false,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'input[type="checkbox"]',
        getAttrs: (dom: string | HTMLElement) => {
          const el = dom as HTMLElement;
          return {
            isChecked: el.getAttribute('checked') === 'true',
            id: el.getAttribute('data-id'),
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const isChecked = HTMLAttributes.isChecked;
    const id = HTMLAttributes.id;
    delete HTMLAttributes.isChecked;
    delete HTMLAttributes.id;
    return [
      'input',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        type: 'checkbox',
        checked: isChecked,
        'data-id': id,
      }),
    ];
  },

  // TODO - if this gets too complex, can consider creating a ReactNodeView
  addNodeView() {
    return ({ node, getPos, editor }) => {
      const wrapper = document.createElement('label');
      wrapper.setAttribute('contenteditable', 'false');
      wrapper.style.cursor = 'pointer';
      wrapper.style.display = 'inline-flex';
      wrapper.style.alignItems = 'center'; // Center vertically
      wrapper.style.verticalAlign = 'middle';
      wrapper.className = 'prosemirror-checkbox-wrapper';

      if (this.options.HTMLAttributes?.style) {
        const existingStyle = wrapper.getAttribute('style');
        wrapper.setAttribute('style', `${existingStyle}; ${this.options.HTMLAttributes.style}`);
      }

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.checked = node.attrs.isChecked;
      input.setAttribute('data-id', node.attrs.id);
      input.style.width = '0px';
      input.style.height = '0px';
      input.style.visibility = 'hidden';

      const span = document.createElement('span');
      span.className = 'prosemirror-checkbox';
      if (this.options.hasCheckedAnimation) {
        span.classList?.add('animated');
      } else if (span.classList?.contains('animated')) {
        span.classList?.remove('animated');
      }

      span.style.display = 'inline-flex';
      span.style.justifyContent = 'center';
      span.style.alignItems = 'center';
      span.style.width = '20px';
      span.style.height = '20px';
      span.style.borderRadius = '4px';
      span.style.padding = '2px';
      span.style.border = '1px solid var(--vg-colors-font-500)';
      span.innerHTML = node.attrs.isChecked ? checkboxIcon : '';

      const handleClick = async () => {
        if (!editor) return;
        if (typeof getPos !== 'function') return;

        // Note: don't need to toggle input, it's handled by default click event handler for <label> element
        const isChecked = !input.checked;

        // Handle onChange event if provided
        let isCheckSuccessful = true;
        if (typeof this.options.onChange === 'function') {
          // Disable the checkbox while the onChange is being called
          input.disabled = true;
          span.style.background = 'var(--vg-colors-gray-200)';
          wrapper.style.cursor = 'not-allowed';
          const oldPointerEvent = wrapper.style.pointerEvents;
          wrapper.style.pointerEvents = 'none';

          // Trigger on change
          try {
            await this.options.onChange(node.attrs.id, isChecked);
          } catch (err) {
            isCheckSuccessful = false;
          }

          // Re-enable the checkbox - set a delay to prevent rapid clicking
          await new Promise((resolve) => setTimeout(resolve, 100));
          wrapper.style.pointerEvents = oldPointerEvent;
          wrapper.style.cursor = 'pointer';
          span.style.background = 'transparent';
          input.disabled = false;
        }

        // If there was an issue with the onChange handler, don't update the checkbox
        if (!isCheckSuccessful) return;

        // Toggle the checkbox on click
        span.innerHTML = isChecked ? checkboxIcon : '';
        const transaction = editor.view.state.tr.setNodeMarkup(getPos(), undefined, {
          ...node.attrs,
          isChecked,
        });
        editor.view.dispatch(transaction);
      };

      wrapper.addEventListener('click', handleClick);

      wrapper.append(input);
      wrapper.append(span);

      return {
        dom: wrapper,
        update: (updatedNode: any) => {
          if (updatedNode.type !== node.type) {
            return false;
          }
          input.checked = updatedNode.attrs.isChecked;
          span.innerHTML = updatedNode.attrs.isChecked ? checkboxIcon : '';
          return true;
        },
        destroy: () => {
          // cleanup event listener
          wrapper?.removeEventListener('click', handleClick);
        },
      };
    };
  },

  addCommands() {
    return {
      // Insert a checkbox into the editor
      insertCheckbox:
        () =>
        ({ commands }) => {
          const id = nanoid();
          return commands.insertContent({
            type: this.name,
            attrs: { id, isChecked: false },
          });
        },
    };
  },
});
